/* eslint-disable complexity */
import _ from 'lodash';
import { getState } from 'src/store';
import { getClientConfigData } from 'src/workspace/utils';
import { HIERARCHY } from './constants';
import { getRevyreWorkflowConfig as getRevyreWorkflowConfigSelector } from './selectors';
import type { RevyreWorkflowConfig } from './types';

export const getCurrentModule = (moduleCode: string) => {
  const config = getClientConfigData();
  if (moduleCode === 'user') {
    return {
      ...(config.user_config || {}),
    };
  }
  const currentModule =
    _.find(config.lead_modules, (module: any) => module.code === moduleCode) ||
    {};
  return currentModule;
};

export const getCurrentModuleFromStartState = (startState: string) => {
  const config = getClientConfigData();
  const currentModule = _.find(
    config.lead_modules,
    (module: any) => module.start_state === startState,
  );
  return currentModule;
};

export const getCurrentUser = () => {
  const config = getClientConfigData();
  const { user = {} } = config;
  return user;
};

export const getIsIsacButtonEnabled = () => {
  const config = getClientConfigData();
  const { isacButtonEnabled } = config;
  return isacButtonEnabled;
};

export const getUserOptionsConfig = () => {
  const config = getClientConfigData();
  const { user_option_configs: userOptionConfigs } = config;
  return userOptionConfigs || {};
};

export const isUserManagementApprovalEnabled = () => {
  const config = getClientConfigData();
  const userManagementConfig = _.get(config, 'user_management_config', {});
  return _.get(userManagementConfig, 'approval_config.enabled', false);
};

export const getFeaturesConfig = () => {
  const config = getClientConfigData();
  const { features_config: featuresConfig } = config || {};
  return featuresConfig || [];
};

export const getModuleRolePermissions = (moduleCode: string) => {
  const config = getClientConfigData();
  return _.get(
    config,
    `leads_authorizations.module_role_permissions${moduleCode}`,
    {},
  );
};

export const getLeadsAuthorizations = () => {
  const config = getClientConfigData();
  return _.get(config, 'leads_authorizations', {});
};

export const getManageUsersActionPermissions = (
  config,
  action,
  defaultValue,
) => {
  const { users_role_permission: usersRolePermission } = config;
  const manageUserActionPermissions = _.get(
    usersRolePermission,
    'manage_user_action_permissions',
    {},
  );
  return _.get(manageUserActionPermissions, action, defaultValue);
};

const getWebActions = (lead) => {
  try {
    const moduleByRolePerms = _.get(
      getLeadsAuthorizations(),
      'module_role_permissions',
      {},
    );
    const module = getCurrentModuleFromStartState(lead) || {};
    const webActions = _.get(moduleByRolePerms, module.code, {});
    return webActions;
  } catch (error) {
    return {};
  }
};
export const isWebOptionEnabled = (option, leadOption, lead) => {
  try {
    const webActions = getWebActions(lead);
    const isRBACFlagEnable = _.get(getFeaturesConfig(), 'rbac', false);
    const module = getCurrentModuleFromStartState(lead) || {};
    const leadsAuthorizations = getLeadsAuthorizations() || {};
    const leadOptionState =
      leadsAuthorizations && leadsAuthorizations[leadOption];
    if (!isRBACFlagEnable) {
      return leadOptionState && !webActions[option];
    }
    if (!_.isEmpty(module)) {
      if (_.isEmpty(webActions) && leadOption) {
        return leadOptionState;
      }
      if (option in webActions) {
        return !webActions[option];
      }
      return false;
    }
    return leadOptionState;
  } catch (error) {
    return false;
  }
};

export const getConfigField = (path) => {
  const config = getClientConfigData();
  return _.get(config, path);
};

export const getRevyreWorkflowConfig = () => {
  const revyreWorkflow = Object.values(
    getRevyreWorkflowConfigSelector(getState()) ?? {},
  ) as RevyreWorkflowConfig[];
  return revyreWorkflow;
};

export const getRevyreWorkflowConfigByCode = (code: string) =>
  (getRevyreWorkflowConfig() ?? []).filter(
    (workflowConfig) => workflowConfig?.code === code,
  );

export const getNonHierarchyRevyreModules = () =>
  (getRevyreWorkflowConfig() ?? []).filter(
    (module) => module.type !== HIERARCHY,
  );

export const getLeadModules = () => {
  const config = getClientConfigData();
  return _.get(config, 'lead_modules', []);
};

export const getLmsWebLoggedin = () => {
  const config = getClientConfigData();
  return !_.isEmpty(config?.auth_token);
};

// Helpers for computing next task states
const determineStartState = (activity: any) => {
  let startState = _.get(activity, 'data.vo.start_state');

  if (
    !startState &&
    _.get(activity, 'data.task.type', '') === 'google_calendar'
  ) {
    startState = _.get(
      getFeaturesConfig(),
      'google_integration.module_first_update_types',
      '',
    );
  }

  return startState;
};

const resolveModuleAndTask = (activity: any, startState?: string) => {
  let module = getCurrentModuleFromStartState(startState || '');
  const tasks = _.get(module, 'tasks', []);
  let task = _.find(tasks, { code: _.get(activity, 'data.task.code') });

  if (
    activity.category === 'USER_CALENDAR_ITEM' ||
    (module && module.code === 'user_tasks')
  ) {
    module = getCurrentModule('user') || {};
    task = _.find(module.tasks, { code: _.get(activity, 'data.task.code') });
  }

  return { module, task };
};

const canTaskBeUpdated = (activity: any, task: any) => {
  let canUpdateTask =
    activity.state !== 'CANCELLED' && activity.state !== 'COMPLETED';
  if ((task?.actions_enabled || []).includes('edit_on_completion')) {
    canUpdateTask = true;
  }
  return canUpdateTask && !_.isEmpty(task?.actions_enabled);
};

const addNextState = (
  nextStates: any[],
  code: string,
  name: string,
  type: string,
) => {
  nextStates.push({ code, name, type });
};

const getIsActivityReassignable = (
  activity: any,
  task: any,
  moduleCode: string,
) => {
  let isAllowedReassign;
  if (
    typeof _.get(getModuleRolePermissions(moduleCode), 'reassign', null) ===
    'boolean'
  ) {
    isAllowedReassign = _.get(getModuleRolePermissions(moduleCode), 'reassign');
  } else {
    isAllowedReassign = _.get(getLeadsAuthorizations(), 'reassign', false);
  }

  return (
    isAllowedReassign &&
    task &&
    task.actions_enabled &&
    task.actions_enabled.indexOf('reassign') !== -1
  );
};

const buildNextStates = (activity: any, task: any, moduleCode: string) => {
  const nextStates: any[] = [];
  const isActivityCompleted = activity.state === 'COMPLETED';
  const actionsEnabledSet = new Set<string>(task.actions_enabled || []);
  const isAllowedReassign = getIsActivityReassignable(
    activity,
    task,
    moduleCode,
  );

  const candidates = [
    {
      cond: !isActivityCompleted && actionsEnabledSet.has('complete'),
      code: 'complete_inputs',
      name: 'Complete',
      type: 'COMPLETE',
    },
    {
      cond: !isActivityCompleted && actionsEnabledSet.has('reschedule'),
      code: 'reschedule_inputs',
      name: 'Reschedule',
      type: 'RESCHEDULE',
    },
    {
      cond: isAllowedReassign,
      code: 'reassign_inputs',
      name: 'Reassign',
      type: 'REASSIGN',
    },
    {
      cond: !isActivityCompleted && actionsEnabledSet.has('edit'),
      code: 'edit_inputs',
      name: 'Edit',
      type: 'EDIT',
    },
    {
      cond: isActivityCompleted && actionsEnabledSet.has('edit_on_completion'),
      code: 'edit_inputs',
      name: 'Edit',
      type: 'EDIT',
    },
    {
      cond: !isActivityCompleted && actionsEnabledSet.has('cancel'),
      code: 'cancel_inputs',
      name: 'Cancel',
      type: 'CANCEL',
    },
  ];

  candidates.forEach((c) => {
    if (c.cond) {
      addNextState(nextStates, c.code, c.name, c.type);
    }
  });

  return nextStates;
};

export const getTaskNextStates = (activity: any = {}) => {
  if (_.isEmpty(activity)) {
    return [];
  }

  const startState = determineStartState(activity);
  const { module, task } = resolveModuleAndTask(activity, startState);

  if (_.isEmpty(module) || _.isEmpty(task)) {
    return [];
  }

  if (!canTaskBeUpdated(activity, task)) {
    return [];
  }

  return buildNextStates(activity, task, module.code);
};

export const getClientCode = () => _.get(getClientConfigData(), 'client');

export const isUserAdminOrSU = () =>
  !_.isEmpty(getCurrentUser())
    ? getCurrentUser()?.isAdmin || getCurrentUser()?.code === 'SU'
    : false;

export const isImpersonationEnabled = (email) =>
  Boolean(getClientConfigData()?.impersonation?.enabled) &&
  email !== getCurrentUser()?.email;

export const isImpersonated = () =>
  Boolean(window.localStorage.getItem('isImpersonated') === 'true');

export const maskValue = (value: string, maskLength: number) => {
  if (!value) return '';

  const len = value.length;

  const effectiveMaskLength = Math.min(maskLength, len - 1);

  const visiblePart = value.slice(len - effectiveMaskLength);
  const maskedPart = '*'.repeat(len - effectiveMaskLength);

  return maskedPart + visiblePart;
};

export const getDisplayValue = (value) => {
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return value?.name || '-';
  return JSON.stringify(value);
};

export const getDashboardSections = () => {
  const config = getClientConfigData();
  const menuSections = _.get(config, 'menu.sections.0.section_items');
  const browserWebView = _.filter(menuSections, { type: 'browser_webview' });
  const dashboardSections = [..._.get(config, 'dashboard_sections', [])];
  const tableauSection = _.get(config, 'tableau_sections', []);

  if (tableauSection) {
    dashboardSections.push(tableauSection);
  }

  if (isUserAdminOrSU() && ['hdfc', 'hdfcbank'].includes(getClientCode())) {
    dashboardSections.push({
      display: true,
      icon: 'fa-dashboard',
      modules: ['onboard'],
      name: 'On board',
      type: 'onboard',
    });
  }

  if (browserWebView.length > 0) {
    dashboardSections.push({
      display: true,
      icon: 'fa fa-tasks',
      modules: ['resource_center'],
      name: 'LINKS',
      type: 'resource_center_links',
    });
  }

  return dashboardSections;
};

export const getRevyreModuleLabelByCode = (code?: string) => {
  if (!code) return undefined;

  const revyreConfig = getRevyreWorkflowConfig() ?? [];

  return revyreConfig.find((config) => config?.code === code)?.name;
};
